import Vue from 'vue'
import axios from 'axios'
import LocalStorage from 'quasar/src/plugins/LocalStorage.js';import Platform from 'quasar/src/plugins/Platform.js';import Notify from 'quasar/src/plugins/Notify.js';

Vue.prototype.$axios = axios
Vue.prototype.$axiosNoIntercept = axios.create()

// Automatically determine API URL based on environment
let apiUrl;

// Check if running on Cordova
const isCordova = typeof window !== 'undefined' && window.cordova !== undefined;

// Check if running on localhost
const isLocalhost = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
   window.location.hostname === '127.0.0.1');

// Set API URL based on conditions
if (!isCordova && isLocalhost) {
  // Local development environment
  //apiUrl = 'http://127.0.0.1:8000/api';
  apiUrl = 'https://womenswellbeing-api.apped.nz/api';
} else {
  // Production environment or Cordova
  apiUrl = 'https://womenswellbeing-api.apped.nz/api';
}

// Set the API URL for use throughout the app
Vue.prototype.$apiUrl = apiUrl;

// Log the selected API URL for debugging
console.log('Using API URL:', apiUrl);

export function copy(object){
  if(!object){
    return object
  }

  return JSON.parse(JSON.stringify(object))
}

export function debounce (fn, delay) {
  var timeoutID = null
  return function () {
    clearTimeout(timeoutID)
    var args = arguments
    var that = this
    timeoutID = setTimeout(function () {
      fn.apply(that, args)
    }, delay)
  }
}

export function convertFrom24To12Format (time24) {
  const [sHours, minutes] = time24.match(/([0-9]{1,2}):([0-9]{2})/).slice(1);
  const period = +sHours < 12 ? 'am' : 'pm';
  const hours = +sHours % 12 || 12;

  return `${hours}:${minutes} ${period}`;
}

export function setAccessToken (token = null) {
  // Persist provided token or load it from storage
  if (typeof token === 'string' && token.trim().length > 0) {
    LocalStorage.set('access_token', token)
  } else {
    token = LocalStorage.getItem('access_token')
  }

  // Set or remove Authorization header depending on token validity
  if (typeof token === 'string' && token.trim().length > 0) {
    const bearer = 'Bearer ' + token
    Vue.prototype.$axios.defaults.headers.common.Authorization = bearer
    Vue.prototype.$axiosNoIntercept.defaults.headers.common.Authorization = bearer
  } else {
    // Remove header when token is empty/invalid to avoid sending "Bearer null"
    delete Vue.prototype.$axios.defaults.headers.common.Authorization
    delete Vue.prototype.$axiosNoIntercept.defaults.headers.common.Authorization
  }
}

setAccessToken()

// Clear token from storage and axios headers
export function clearAccessToken () {
  // Remove stored token to avoid using an invalid value later
  LocalStorage.remove('access_token')

  // Ensure axios instances do not send stale Authorization headers
  delete Vue.prototype.$axios.defaults.headers.common.Authorization
  delete Vue.prototype.$axiosNoIntercept.defaults.headers.common.Authorization
}

// Keep Authorization header in sync with LocalStorage on every request
Vue.prototype.$axios.interceptors.request.use((config) => {
  const token = LocalStorage.getItem('access_token')
  if (typeof token === 'string' && token.trim().length > 0) {
    config.headers.Authorization = 'Bearer ' + token
  } else {
    delete config.headers.Authorization
  }
  return config
})

// Handle unauthorized responses globally
Vue.prototype.$axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error && error.response && error.response.status
    if (status === 401 || status === 403 || status === 419) {
      // When token is invalid/expired, clear it to stop error cascades
      clearAccessToken()
    }
    
    // Detailed error logging to help diagnose API issues
    // NOTE: All logs are in English for clarity
    /*
     * We log a compact, yet useful error payload:
     * - HTTP method and URL
     * - Status code
     * - Response message/body if available
     * - Request data (safe preview)
     */
    try {
      const config = error && error.config ? error.config : {}
      const method = (config.method || 'GET').toUpperCase()
      const url = config.url || 'unknown-url'
      const responseData = error && error.response && error.response.data ? error.response.data : null
      // Avoid dumping huge payloads into console
      const requestData = config && config.data ? (typeof config.data === 'string' ? config.data : JSON.stringify(config.data)) : null
      // eslint-disable-next-line no-console
      console.error('[API ERROR]', {
        method,
        url,
        status,
        response: responseData,
        requestData
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[API ERROR] Failed to log error details')
    }

    // Show a top-centered notification to inform the user
    // Always use English text in UI notifications
    if (status >= 500 || !status) {
      Notify.create({
        message: 'Server error. Please try again later.',
        color: 'negative',
        icon: 'warning',
        position: 'top',
        timeout: 4000
      })
    } else if (status >= 400) {
      const message = getErrorMessage(error)
      if (message) {
        Notify.create({
          message,
          color: 'negative',
          icon: 'warning',
          position: 'top',
          timeout: 4000
        })
      }
    }

    return Promise.reject(error)
  }
)

export async function getUser () {
  return Vue.prototype.$axiosNoIntercept.get(Vue.prototype.$apiUrl + '/user/me').then((response) => {
    // Get Token
    return response.data
  })
}

export async function storeFcmToken (token) {
  if (!token) {
    return
  }

  return Vue.prototype.$axiosNoIntercept.post(Vue.prototype.$apiUrl + '/auth/store_token', {
    'token': token, 'platform': Platform.is.platform
  }).then((response) => {
    return response.data
  })
}

// Extract human-readable error message from Axios error
// Returns empty string if nothing useful can be derived
export function getErrorMessage (error) {
  try {
    if (!error) return ''
    const response = error.response
    if (response && response.data) {
      // Common Laravel API patterns: { message: string } or { error: string } or validation errors
      if (typeof response.data.message === 'string' && response.data.message.trim().length > 0) {
        return response.data.message
      }
      if (typeof response.data.error === 'string' && response.data.error.trim().length > 0) {
        return response.data.error
      }
      if (response.data.errors && typeof response.data.errors === 'object') {
        const firstKey = Object.keys(response.data.errors)[0]
        const firstVal = response.data.errors[firstKey]
        if (Array.isArray(firstVal) && firstVal.length > 0) {
          return String(firstVal[0])
        }
      }
    }
    // Fallback to generic message
    if (error.message) return error.message
  } catch (e) {
    // ignore
  }
  return ''
}
